import { Alert, Button, Field, Modal } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FormEvent, type JSX } from 'react';
import { type Resolver, useForm, useWatch } from 'react-hook-form';

import {
  DEFAULT_MENU_CALENDAR_ENTRY_FORM_VALUES,
  menuCalendarEntryFormSchema,
  type MenuCalendarEntryFormValues,
} from '../../../calendar/menu-calendar-entry-form-schema';

export interface MenuCalendarEntryModalMenuOption {
  id: string;
  name: string;
  serviceTypeId: string;
}

export interface MenuCalendarEntryModalPositionOption {
  id: string;
  name: string;
  serviceTypeId: string;
}

export interface MenuCalendarEntryModalProps {
  mode: 'create' | 'edit';
  defaultValues?: MenuCalendarEntryFormValues | undefined;
  menus: MenuCalendarEntryModalMenuOption[];
  positions: MenuCalendarEntryModalPositionOption[];
  submitting: boolean;
  formError?: string | undefined;
  onCancel: () => void;
  onSubmit: (values: MenuCalendarEntryFormValues) => Promise<void>;
}

/**
 * `menu-calendar/spec.md` → "Alta de entrada de calendario" / "Edición de
 * entrada de calendario". `serviceDate`/`menuId` render read-only in edit
 * mode: both form the entry's uniqueness key and the API rejects changing
 * them (`MENU_CALENDAR_IMMUTABLE_FIELDS`), same criterion
 * `ServiceTypeForm` already applies to `code`.
 */
export function MenuCalendarEntryModal({
  mode,
  defaultValues,
  menus,
  positions,
  submitting,
  formError,
  onCancel,
  onSubmit,
}: MenuCalendarEntryModalProps): JSX.Element {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<MenuCalendarEntryFormValues, unknown, MenuCalendarEntryFormValues>({
    resolver: zodResolver(menuCalendarEntryFormSchema) as unknown as Resolver<
      MenuCalendarEntryFormValues,
      unknown,
      MenuCalendarEntryFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_MENU_CALENDAR_ENTRY_FORM_VALUES,
  });

  function handleFormSubmit(event: FormEvent): void {
    void handleSubmit((values) => onSubmit(values))(event);
  }

  const isEdit = mode === 'edit';
  const selectedPositionId = useWatch({ control, name: 'positionId' });
  const selectedPosition = positions.find((position) => position.id === selectedPositionId);
  const menuOptions =
    selectedPosition === undefined
      ? []
      : menus.filter((menu) => menu.serviceTypeId === selectedPosition.serviceTypeId);

  return (
    <Modal
      open
      onClose={onCancel}
      title={isEdit ? 'Editar entrada de calendario' : 'Nueva entrada de calendario'}
      className="max-w-lg"
    >
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <Field
          label="Fecha de servicio"
          type="date"
          required
          disabled={isEdit}
          {...register('serviceDate')}
          {...maybeError(errors.serviceDate?.message)}
        />

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="menu-calendar-entry-position"
            className="text-sm font-medium text-slate-900"
          >
            Posición
          </label>
          <select
            id="menu-calendar-entry-position"
            disabled={isEdit}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50 disabled:text-slate-500"
            {...register('positionId', { onChange: () => setValue('menuId', '') })}
          >
            <option value="">Seleccioná una posición</option>
            {positions.map((position) => (
              <option key={position.id} value={position.id}>
                {position.name}
              </option>
            ))}
          </select>
          {errors.positionId?.message === undefined ? null : (
            <p role="alert" className="text-xs text-red-600">
              {errors.positionId.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="menu-calendar-entry-menu" className="text-sm font-medium text-slate-900">
            Menú
          </label>
          <select
            id="menu-calendar-entry-menu"
            disabled={isEdit || selectedPosition === undefined}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50 disabled:text-slate-500"
            {...register('menuId')}
          >
            <option value="">
              {selectedPosition === undefined
                ? 'Elegí primero una posición'
                : 'Seleccioná un menú'}
            </option>
            {menuOptions.map((menu) => (
              <option key={menu.id} value={menu.id}>
                {menu.name}
              </option>
            ))}
          </select>
          {errors.menuId?.message === undefined ? null : (
            <p role="alert" className="text-xs text-red-600">
              {errors.menuId.message}
            </p>
          )}
        </div>

        <Field
          label="Cupo máximo"
          hint="Dejalo vacío para no fijar un tope"
          type="number"
          min={1}
          {...register('maxQuantity')}
          {...maybeError(errors.maxQuantity?.message)}
        />

        <Field label="Notas" {...register('notes')} {...maybeError(errors.notes?.message)} />

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            {isEdit ? 'Guardar cambios' : 'Crear entrada'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}
